import type { ICampaignTemplate } from '~/modules/campaign-template/types';
import type { AssetTemplate, ShopifyAsset } from '~/modules/shopify/types';
import type { Variant } from '~/modules/campaign/types';
import type { IThemePage } from '~/stores/themePage';

import { mapThemePageInfo, getSuffixTemplateV7By } from '~/utils';
import { getAssetKey } from '~/presentation/common';
import { ProviderSource } from '~/types/template';

export const createAssetTemplate = ({
  asset,
  themePages,
}: {
  asset: ShopifyAsset;
  themePages?: IThemePage[];
}): AssetTemplate => {
  const assetKey = getAssetKey(asset, themePages);
  const themePageInfo = mapThemePageInfo(asset.key, themePages, true);

  return {
    ...asset,
    key: assetKey,
    updated_at: themePageInfo?.updatedAt ?? asset.updated_at,
    themePageInfo: themePageInfo
      ? {
          ...themePageInfo,
          updatedAt: themePageInfo?.updatedAt ?? asset.updated_at,
        }
      : undefined,
  };
};

export const createDraftAssetTemplate = (page: IThemePage): AssetTemplate => {
  const key = getSuffixTemplateV7By(page.id, page.type);
  const themePageInfo = mapThemePageInfo(key, [page], true);

  return {
    key,
    providerSource: ProviderSource.GEMPAGESV7,
    updated_at: page.updatedAt,
    assigned: {
      total: 0,
      url: '',
      data: [],
    },
    themePageInfo,
  };
};

export const clearTemplateFromCampaign = async (
  campaign: ICampaignTemplate,
  templateKey: string,
  clearVariant: (variant: Variant) => Promise<Variant>,
): Promise<ICampaignTemplate> => {
  if (!campaign.variants) return campaign;

  const matchedVariants = campaign.variants.filter(
    (v): v is Variant => v?.templateSuffix === templateKey,
  );
  if (!matchedVariants.length) return campaign;

  // When the removed template is on the Control side, the experiment's pair becomes invalid,
  // so cascade-clear the Variant side too. Removing only the Variant side keeps Control intact.
  const isControlRemoved = matchedVariants.some((v) => v.isOrigin);
  const variantsToUpdate = isControlRemoved
    ? campaign.variants.filter((v): v is Variant => !!v)
    : matchedVariants;

  const clearedVariants = await Promise.all(variantsToUpdate.map(clearVariant));
  const clearedVariantMap = new Map(clearedVariants.map((v) => [v.id, v]));

  const newVariants = campaign.variants.map((v) => {
    const variantId = v?.id;
    if (variantId && clearedVariantMap.has(variantId)) {
      return clearedVariantMap.get(variantId)!;
    }

    return v;
  });

  return {
    ...campaign,
    variants: newVariants,
  };
};
